import { useState } from 'react';

import { Box } from '@zillow/constellation-10';

import {
  type AccountViewListingGroupDto,
  AccountViewListingGroupType,
  type AccountViewUnitCountsDto,
  type RentalManagerAddressDto,
} from '@zg-rentals/ts-rental-manager-api-client';

import { usePortfolioFilters } from '../../../contexts/PortfolioFiltersContext';
import { getFormattedAddress, getPropertyRoute, isMultiplexListing } from '../../../utils/flexiblePropertyCardUtils';

import { AccordionCard } from '../../../components/AccordionCardList';
import { accordionHeaderWithTrailingControlStyles } from '../../../components/AccordionCardList/styles';
import EditMultiplexPropertyModal from '../../../components/modals/EditMultiplexPropertyModal';
import FlexibleArchivePropertyModal, {
  ARCHIVE_TARGET_NOUN,
  type ArchiveTargetNoun,
} from '../../../components/modals/FlexibleArchivePropertyModal';
import PropertyDetailsModal from '../../../components/modals/PropertyDetailsModal';

import { unitsTableContainerStyles } from '../../../styles/propertyCardTable';
import { overflowMenuStyles } from '../styles';

import FlexiblePropertyCardHeader from './FlexiblePropertyCardHeader';
import PropertyOverflowMenu from './PropertyOverflowMenu';
import UnitsTable from './UnitsTable';

type Props = {
  listing: AccountViewListingGroupDto;
  isExpanded: boolean;
  isLoading?: boolean;
};

type ArchiveTarget = {
  listingAlias: string;
  groupId: string;
  address: RentalManagerAddressDto;
  targetNoun: ArchiveTargetNoun;
  listingType: AccountViewListingGroupType;
  isArchived?: boolean;
  unitNumber?: string;
  unitCount?: number;
};

const FlexiblePropertyCard = ({ listing, isExpanded, isLoading = false }: Props) => {
  const { includeArchived: isArchiveView } = usePortfolioFilters();

  const [archiveTarget, setArchiveTarget] = useState<ArchiveTarget | null>(null);
  const [showMultiplexEditModal, setShowMultiplexEditModal] = useState(false);
  const [showUnitsEditModal, setShowUnitsEditModal] = useState(false);
  const [editListingAlias, setEditListingAlias] = useState('');
  const [pendingLease, setPendingLease] = useState<{ fromVacant?: number; count: number } | undefined>(undefined);
  const isMultiplex = isMultiplexListing(listing);
  const { unitCount } = listing as AccountViewListingGroupDto & { unitCount?: AccountViewUnitCountsDto };
  const serverVacant = unitCount?.vacant;
  const vacantAdjustment = pendingLease && pendingLease.fromVacant === serverVacant ? pendingLease.count : 0;
  const formattedAddress = getFormattedAddress(listing);
  // Accordion.Panel keeps children mounted while collapsed; without this guard every
  // collapsed card would fetch its units, statuses, and tenants.
  const unitsPanel = isExpanded && (
    <Box data-testid="UnitsTableContainer" css={unitsTableContainerStyles}>
      <UnitsTable
        listing={listing}
        onTenantAdded={() =>
          setPendingLease((pending) =>
            pending && pending.fromVacant === serverVacant
              ? { ...pending, count: pending.count + 1 }
              : { fromVacant: serverVacant, count: 1 },
          )
        }
        onArchiveClick={(listingAlias, address, unitNumber) =>
          setArchiveTarget({
            listingAlias,
            groupId: listing.groupId,
            address,
            targetNoun: ARCHIVE_TARGET_NOUN.UNIT,
            listingType: AccountViewListingGroupType.LISTING,
            isArchived: isArchiveView,
            unitNumber,
          })
        }
        onEditClick={(listingAlias) => {
          setEditListingAlias(listingAlias);
          setShowUnitsEditModal(true);
        }}
      />
    </Box>
  );
  // Multifamily listings have no separate units, so the card links to the listing instead of expanding.
  const variantProps =
    !isLoading && listing.isMultifamily && !isMultiplex
      ? ({ variant: 'link', href: getPropertyRoute(listing) ?? undefined, linkLabel: formattedAddress } as const)
      : ({
          variant: 'expandable',
          expandTriggerTestId: 'FlexiblePropertyCard-ExpandTrigger',
          children: unitsPanel,
        } as const);

  return (
    <>
      <AccordionCard
        data-testid="FlexiblePropertyCard"
        {...variantProps}
        header={
          <FlexiblePropertyCardHeader listing={listing} isLoading={isLoading} vacantAdjustment={vacantAdjustment} />
        }
        headerCss={accordionHeaderWithTrailingControlStyles}
        trailingControl={
          <Box css={overflowMenuStyles}>
            <PropertyOverflowMenu
              groupId={listing.groupId}
              listingAlias={listing.listingAlias}
              addressLabel={formattedAddress}
              address={listing.address}
              onArchiveClick={(listingAlias, address) =>
                setArchiveTarget({
                  listingAlias,
                  groupId: listing.groupId,
                  address,
                  targetNoun: ARCHIVE_TARGET_NOUN.PROPERTY,
                  listingType: listing.type,
                  isArchived: isArchiveView,
                  unitCount: listing.listingCount?.total,
                })
              }
              onEditClick={
                isMultiplex
                  ? () => {
                      setShowMultiplexEditModal(true);
                    }
                  : () => {
                      setEditListingAlias(listing.listingAlias);
                      setShowUnitsEditModal(true);
                    }
              }
            />
          </Box>
        }
      />

      {archiveTarget && (
        <FlexibleArchivePropertyModal
          onClose={() => setArchiveTarget(null)}
          isArchived={archiveTarget.isArchived ?? false}
          listingAlias={archiveTarget.listingAlias}
          groupId={archiveTarget.groupId}
          address={archiveTarget.address}
          targetNoun={archiveTarget.targetNoun}
          listingType={archiveTarget.listingType}
          unitNumber={archiveTarget.unitNumber}
          unitCount={archiveTarget.unitCount}
        />
      )}

      {showMultiplexEditModal && (
        <EditMultiplexPropertyModal
          isOpen={true}
          groupId={listing.groupId}
          fullAddress={formattedAddress}
          address={listing.address}
          unitCount={listing.listingCount?.total}
          onClose={() => setShowMultiplexEditModal(false)}
          onSuccess={() => setShowMultiplexEditModal(false)}
        />
      )}

      {showUnitsEditModal && (
        <PropertyDetailsModal
          isOpen={true}
          listingAlias={editListingAlias}
          submitLabel="Save changes"
          onClose={() => setShowUnitsEditModal(false)}
          onConfirm={() => setShowUnitsEditModal(false)}
        />
      )}
    </>
  );
};

export default FlexiblePropertyCard;
